import type { IPagination } from '@experiences/interfaces';
import urljoin from 'url-join';

import {
    axiosDelete,
    get,
    getFile,
    post,
    put,
} from '../utility/Requests';
import { getIdentityBaseRoute } from './IdentityServiceUtils';

interface ConsentContent {
    title: string;
    content: string;
    acceptRadioContent: string;
    declineRadioContent: string;
}

interface ConsentBase {
    recurrenceInDays: number | null;
    content?: ConsentContent;
    isActive: boolean;
    priority: number;
}

export interface Consent extends ConsentBase {
    id: string;
    partitionId: string;
    isDeleted?: boolean;
    creationTime: Date;
    lastModificationTime?: Date | null;
    deletionTime?: Date | null;

    createdBy?: string;
    userRecordCount?: number;
    title?: string; // Flattened title for search functionality
}

interface UserRecordsResponse {
    totalCount: number;
    results: ConsentUserRecord[];
}

export interface ConsentUserRecord {
    id: string; // this is user ID but I map it to ID for the grid
    partitionId: number;
    consentId: string;
    createdOn: string;
    userName: string;
    userEmail: string;
    lastUpdateTime: string;
    consentAccepted: string;
}

interface CreateConsentPayload extends ConsentBase {}
interface UpdateConsentPayload extends ConsentBase {
    userReconsentNeeded: boolean;
}

// Direct calls to Identity service (isHostMode hardcoded as false)
const getConsentsBaseUrl = (partitionGlobalId: string, consentId: string = '') => {
    const baseUrl = urljoin(getIdentityBaseRoute(partitionGlobalId, false), 'api', 'PartitionConsent');
    return urljoin(baseUrl, partitionGlobalId, consentId);
};

export async function getConsents({ partitionGlobalId }: { partitionGlobalId: string }) {
    const consent = await get<Consent>(getConsentsBaseUrl(partitionGlobalId), { ignoreBasePath: true });

    // Apply data transformation from backend: parse JSON content
    if (consent?.content) {
        try {
            consent.content = JSON.parse((consent.content as any) ?? '{}');
        } catch {
            // Handle JSON parse error gracefully - backend would return 500, we'll just leave it as is
        }
    }

    return consent?.content ? [ consent ] : [];
}

export async function getAllConsents({ partitionGlobalId }: { partitionGlobalId: string }) {
    const consents = await get<Consent[]>(urljoin(getConsentsBaseUrl(partitionGlobalId), 'all'), { ignoreBasePath: true });

    // Apply data transformation from backend: parse JSON content for each consent
    return consents.map((consent) => {
        try {
            return {
                ...consent,
                content: JSON.parse(consent.content as any || '{}'),
            };
        } catch {
            return consent;
        }
    });
}

export async function getConsentUserRecords({
    consentId, partitionGlobalId, pagination,
}: { consentId: string; partitionGlobalId: string; pagination: IPagination }) {
    const response = await get<UserRecordsResponse>(urljoin(getConsentsBaseUrl(partitionGlobalId, consentId), 'UserConsents'), {
        pagination,
        ignoreBasePath: true,
    });

    // Apply data transformation from backend: map user records
    const userRecords = response.results.map((record: any) => {
        record.id = record.userId;
        record.userEmail = `${record.userName} (${record.userEmail})`;
        return record;
    });

    return {
        totalCount: response.totalCount,
        results: userRecords,
    };
}

export async function exportUserRecords({
    consentId, partitionGlobalId,
}: { consentId: string; partitionGlobalId: string }) {
    return await getFile(urljoin(getConsentsBaseUrl(partitionGlobalId, consentId), 'Download'), { ignoreBasePath: true });
}

export async function createNewConsent({
    partitionGlobalId, body,
}: { partitionGlobalId: string; body: CreateConsentPayload }) {
    // Apply data transformation from backend: stringify content
    const transformedBody = {
        ...body,
        content: JSON.stringify(body.content),
    };

    return await post<CreateConsentPayload>(getConsentsBaseUrl(partitionGlobalId), {
        body: transformedBody,
        ignoreBasePath: true,
    });
}

export async function getConsentById({
    partitionGlobalId, consentId,
}: { partitionGlobalId: string; consentId: string }) {
    const response = await get<Consent>(getConsentsBaseUrl(partitionGlobalId, consentId), { ignoreBasePath: true });

    // Apply data transformation from backend: parse JSON content
    try {
        return {
            ...response,
            content: JSON.parse(response.content as any || '{}'),
        };
    } catch {
        return undefined;
    }
}

export async function updateConsent({
    partitionGlobalId, consentId, body,
}: { partitionGlobalId: string; consentId: string; body: UpdateConsentPayload }) {
    // Apply data transformation from backend: stringify content
    const transformedBody = {
        ...body,
        content: JSON.stringify(body.content),
    };

    return await put<UpdateConsentPayload>(getConsentsBaseUrl(partitionGlobalId, consentId), {
        body: transformedBody,
        ignoreBasePath: true,
    });
}

export async function deleteConsent({
    partitionGlobalId, consentId,
}: { partitionGlobalId: string; consentId: string }) {
    return await axiosDelete(getConsentsBaseUrl(partitionGlobalId, consentId), { ignoreBasePath: true });
}
